/**
 * Service Worker Registration and Management
 * Handles service worker lifecycle and cache management
 */

class ServiceWorkerManager {
    constructor() {
        this.registration = null;
        this.isSupported = 'serviceWorker' in navigator;
    }

    /**
     * Register service worker
     */
    async register() {
        if (!this.isSupported) {
            console.log('Service Worker not supported');
            return false;
        }

        try {
            this.registration = await navigator.serviceWorker.register('/sw.js', {
                scope: '/',
                updateViaCache: 'none'
            });

            console.log('Service Worker registered successfully:', this.registration);

            // Handle updates
            this.registration.addEventListener('updatefound', () => {
                const newWorker = this.registration.installing;
                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        // New content is available, notify user
                        this.notifyUpdate();
                    }
                });
            });

            // Check for updates periodically so users receive fresh versions faster.
            setInterval(() => {
                this.registration?.update();
            }, 10 * 60 * 1000);

            return true;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
            return false;
        }
    }

    /**
     * Notify user about available update
     */
    notifyUpdate() {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Blog Update Available', {
                body: 'New blog content is available. Refresh to see updates.',
                icon: '/icon-192.png',
                tag: 'blog-update'
            });
        }
    }

    /**
     * Request notification permission
     */
    async requestNotificationPermission() {
        if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        }
        return Notification.permission === 'granted';
    }

    /**
     * Clear all caches
     */
    async clearAllCaches() {
        if (!this.registration) return false;

        try {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => caches.delete(cacheName))
            );
            console.log('All caches cleared');
            return true;
        } catch (error) {
            console.error('Failed to clear caches:', error);
            return false;
        }
    }

    /**
     * Clear specific cache
     */
    async clearCache(cacheName) {
        try {
            await caches.delete(cacheName);
            console.log('Cache cleared:', cacheName);
            return true;
        } catch (error) {
            console.error('Failed to clear cache:', error);
            return false;
        }
    }

    /**
     * Preload blog data
     */
    async preloadBlogData() {
        if (!this.registration) return false;

        try {
            const messageChannel = new MessageChannel();

            return new Promise((resolve) => {
                messageChannel.port1.onmessage = (event) => {
                    if (event.data.type === 'PRELOAD_COMPLETE') {
                        resolve(true);
                    }
                };

                this.registration.active.postMessage(
                    { type: 'PRELOAD_BLOG' },
                    [messageChannel.port2]
                );
            });
        } catch (error) {
            console.error('Failed to preload blog data:', error);
            return false;
        }
    }

    /**
     * Get cache status
     */
    async getCacheStatus() {
        if (!this.registration) return null;

        try {
            const messageChannel = new MessageChannel();

            return new Promise((resolve) => {
                messageChannel.port1.onmessage = (event) => {
                    if (event.data.type === 'CACHE_STATUS') {
                        resolve(event.data.payload);
                    }
                };

                this.registration.active.postMessage(
                    { type: 'GET_CACHE_STATUS' },
                    [messageChannel.port2]
                );
            });
        } catch (error) {
            console.error('Failed to get cache status:', error);
            return null;
        }
    }

    /**
     * Check if service worker is controlling the page
     */
    isControlling() {
        return navigator.serviceWorker.controller !== null;
    }

    /**
     * Get service worker state
     */
    getState() {
        if (!this.registration) return 'unregistered';

        if (this.registration.active) {
            return this.registration.active.state;
        }

        if (this.registration.installing) {
            return 'installing';
        }

        if (this.registration.waiting) {
            return 'waiting';
        }

        return 'unknown';
    }

    /**
     * Force update service worker
     */
    async forceUpdate() {
        if (!this.registration) return false;

        try {
            await this.registration.update();
            return true;
        } catch (error) {
            console.error('Failed to update service worker:', error);
            return false;
        }
    }

    /**
     * Skip waiting and activate new service worker
     */
    async skipWaiting() {
        if (!this.registration || !this.registration.waiting) return false;

        try {
            this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            return true;
        } catch (error) {
            console.error('Failed to skip waiting:', error);
            return false;
        }
    }
}

// Create singleton instance
const serviceWorkerManager = new ServiceWorkerManager();

export default serviceWorkerManager;
